export interface BreadcrumbEntry {
    label: string;
    path?: string;
    filters?: Record<string, string>;
    /** Path of the page this entry was pushed for (the drill-down destination). */
    pushedFor?: string;
}

const STORAGE_KEY = 'breadcrumb_stack';
const DRILLDOWN_KEY = 'breadcrumb_drilldown';

// Cached snapshot so useSyncExternalStore gets a stable reference between
// mutations (getSnapshot must not return a fresh array each call).
let cachedJson: string | null = null;
let cachedStack: BreadcrumbEntry[] = [];

const listeners = new Set<() => void>();

function read(): BreadcrumbEntry[] {
    const json = sessionStorage.getItem(STORAGE_KEY) || '[]';
    if (json !== cachedJson) {
        try {
            cachedStack = JSON.parse(json);
        } catch {
            cachedStack = [];
        }
        cachedJson = json;
    }
    return cachedStack;
}

function save(stack: BreadcrumbEntry[]) {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stack));
    listeners.forEach(fn => fn());
}

function get(): BreadcrumbEntry[] {
    return read();
}

function subscribe(fn: () => void): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
}

// Paths carry the screen's query string (userFilter, pagination), which can
// differ between visits to the same screen - comparisons must ignore it.
function pathWithoutQuery(path?: string): string {
    return (path || '').split('?')[0];
}

/**
 * Push the trail entries describing the CURRENT page before navigating away.
 * `destPath` is the path being navigated to - it tags the entries so heal()
 * can later tell which page they belong to (survives refresh/back/forward).
 */
function push(entries: BreadcrumbEntry[], destPath: string) {
    const stack = get();
    const dest = pathWithoutQuery(destPath);
    const tagged = entries.map(e => ({ ...e, pushedFor: dest }));

    // Navigating to a page that is already an ancestor in the trail means
    // we're looping back to it (e.g. detail -> link back to its own screen).
    // Truncate to that point instead of stacking a longer and longer loop -
    // same semantics as clicking that crumb. Exception: a screen drilling
    // into ITSELF (self-recursive hierarchies, same URL) is not a loop back -
    // that case is handled by the entry check below (truncate + re-push).
    const selfRecursive = entries.length > 0 && pathWithoutQuery(entries[0].path) === dest;
    if (!selfRecursive) {
        const destIndex = stack.findIndex(e => pathWithoutQuery(e.path) === dest);
        if (destIndex !== -1) {
            save(stack.slice(0, destIndex));
            return;
        }
    }

    // Detect loops: if the first entry being pushed matches an existing entry
    // in the stack (same label + same path), truncate to that point first so
    // the trail doesn't grow forever on A -> B -> A cycles.
    if (entries.length > 0 && entries[0].path) {
        const existingIndex = stack.findIndex(
            e => pathWithoutQuery(e.path) === pathWithoutQuery(entries[0].path) && e.label === entries[0].label
        );
        if (existingIndex !== -1) {
            save([...stack.slice(0, existingIndex), ...tagged]);
            return;
        }
    }

    save([...stack, ...tagged]);
}

/**
 * Self-heal the stack for the page at `currentPath`. Called on POP navigations
 * (browser back / forward / refresh) - instead of blindly popping, reconcile
 * the stack against where we actually landed:
 *  - entries pushed FOR this page are its ancestors -> keep through the last one;
 *  - an entry whose own path IS this page is a leftover from drilling deeper
 *    and coming back -> cut it (and everything after it) off;
 *  - nothing matches (e.g. browser forward to a page whose entries were already
 *    healed away) -> leave the stack alone rather than corrupt it.
 */
function heal(currentPath: string) {
    const stack = get();
    if (stack.length === 0) return;

    const p = pathWithoutQuery(currentPath);

    for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].pushedFor === p) {
            if (i < stack.length - 1) {
                save(stack.slice(0, i + 1));
            }
            return;
        }
    }

    const selfIndex = stack.findIndex(e => pathWithoutQuery(e.path) === p);
    if (selfIndex !== -1) {
        save(stack.slice(0, selfIndex));
    }
}

function truncateTo(index: number): BreadcrumbEntry | null {
    const stack = get();
    const entry = stack[index] || null;
    save(stack.slice(0, index));
    return entry;
}

function pop(count: number = 1) {
    const stack = get();
    save(stack.slice(0, Math.max(0, stack.length - count)));
}

function clear() {
    save([]);
}

/** Mark the next PUSH navigation as a drill-down (keeps the stack). */
function markDrilldown() {
    sessionStorage.setItem(DRILLDOWN_KEY, 'true');
}

/** Consume the drill-down flag; returns whether it was set. */
function consumeDrilldown(): boolean {
    const set = sessionStorage.getItem(DRILLDOWN_KEY) === 'true';
    sessionStorage.removeItem(DRILLDOWN_KEY);
    return set;
}

export const breadcrumbStack = {
    get,
    subscribe,
    push,
    heal,
    truncateTo,
    pop,
    clear,
    markDrilldown,
    consumeDrilldown,
};
